import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { Overview } from './pages/Overview'
import { Explorer } from './pages/Explorer'
import { Projects } from './pages/Projects'
import { ProjectForm } from './pages/ProjectForm'
import { Admin } from './pages/Admin'
import { Intake } from './pages/Intake'
import { Alerts } from './pages/Alerts'
import { AiAccess } from './pages/AiAccess'
import { SensorInfo } from './pages/SensorInfo'
import { CalculatedSensors } from './pages/CalculatedSensors'
import { ManualSensors } from './pages/ManualSensors'
import { SignIn } from './pages/SignIn'
import { Pending } from './pages/Pending'
import { Gate } from './lib/auth'
import { PageBoundary } from './components/PageBoundary'
import { WorkspaceShell } from './components/WorkspaceShell'

// MapLibre is most of a megabyte: only the Map page pays for it, when opened.
const MapPage = lazy(() => import('./pages/Map').then((m) => ({ default: m.MapPage })))
// Analyses and the comparison are the next heaviest, the same way.
const WorkbookLibrary = lazy(() => import('./pages/Workbooks').then((m) => ({ default: m.WorkbookLibrary })))
const WorkbookPage = lazy(() => import('./pages/Workbooks').then((m) => ({ default: m.WorkbookPage })))
const Compare = lazy(() => import('./pages/Compare').then((m) => ({ default: m.Compare })))
const Dashboards = lazy(() => import('./pages/Dashboards').then((m) => ({ default: m.Dashboards })))

/** Pages that stand alone, without the station-network header. */
const BARE = ['/login', '/pending']
/** Full-height tools with no footer (phase 6): the map and the comparison
 *  fill the window under the header. */
const FULL = ['/map']

export function App() {
  const { pathname } = useLocation()
  const bare = BARE.includes(pathname)
  const full = FULL.includes(pathname)

  const content = (
    <PageBoundary resetKey={pathname}>
    <Gate>
          {/* Every lazy page waits here: with no Suspense above it, React throws on the
              first navigation to one. One that fails to load, after a deploy, reaches
              routes.tsx's RouteError. */}
          <Suspense fallback={<p className="pad muted">Loading…</p>}>
          <Routes>
            <Route path="/login" element={<SignIn />} />
            <Route path="/pending" element={<Pending />} />
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<Overview />} />
            {/* The Data Explorer opens on the analyses themselves (Tamar,
                2026-11-01); the quick look is Station readings. */}
            <Route path="/analysis" element={<WorkbookLibrary />} />
            <Route path="/analysis/:id" element={<WorkbookPage />} />
            <Route path="/dashboards" element={<Dashboards />} />
            <Route path="/dashboards/:id" element={<Dashboards />} />
            <Route path="/sensors/calculated" element={<CalculatedSensors />} />
            <Route path="/sensors/manual" element={<ManualSensors />} />
            <Route path="/sensors/manual/:id" element={<ManualSensors />} />
            <Route path="/stations" element={<StationsMoved />} />
            <Route path="/explore" element={<Explorer />} />
            <Route path="/explore/:code" element={<Explorer />} />
            <Route path="/explore/:code/:instrument" element={<Explorer />} />
            {/* The Analytics page's tools are analysis types (2026-11-01). */}
            <Route path="/analytics" element={<Navigate to="/analysis" replace />} />
            <Route path="/analytics/:code" element={<Navigate to="/analysis" replace />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/new" element={<ProjectForm />} />
            <Route path="/projects/:id/edit" element={<ProjectForm />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/ai-access" element={<AiAccess />} />
            <Route path="/map" element={
              <Suspense fallback={<p className="pad muted">Loading the map…</p>}><MapPage /></Suspense>
            } />
            <Route path="/sensors/info" element={<SensorInfo />} />
            <Route path="/sensors/info/:code" element={<SensorInfo />} />
            <Route path="/sensors/info/:code/:sensor" element={<SensorInfo />} />
            <Route path="/sensors/compare" element={<Compare />} />
            <Route path="/admin/intake" element={<Intake />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<p className="pad">Not found.</p>} />
          </Routes>
          </Suspense>
    </Gate>
    </PageBoundary>
  )

  if (bare) return <div className="app workspace-bare"><main>{content}</main></div>
  return <WorkspaceShell full={full}>{content}</WorkspaceShell>
}

/** The Stations page became part of Overview (owner, 2026-10-22). Old links
 *  and bookmarks keep working, with their search and filters. */
function StationsMoved() {
  const { search } = useLocation()
  return <Navigate to={`/overview${search}`} replace />
}
